import React from 'react';
import { useHistory } from 'react-router';
import {
    getResponseInitialState,
    getResponseProps,
    useAlert,
    useFetch
} from '../../hooks';
import {
    authReducer,
    authReducerInitialState,
    authReducerInit,
    authReducerProps,
    authReducerActionTypes,
    sessionProps
} from './auth.reducer';

interface AuthContextProps extends authReducerProps {
    auth: authReducerProps;
    dispatchAuth: React.Dispatch<authReducerActionTypes>;
    logoutAlert: boolean;
    closeLogoutAlert: () => void;
    logout: () => void;
    checkValidSession: (
        checkSession: sessionProps,
        type: 'login' | 'checkSession'
    ) => void;
    getUserInfo: () => Promise<void>;
    getUserInfoResponse: getResponseProps;
}

export const AuthContext = React.createContext<AuthContextProps>({
    ...authReducerInitialState,
    auth: authReducerInitialState,
    logoutAlert: false,
    dispatchAuth: () => {},
    closeLogoutAlert: () => {},
    checkValidSession: () => {},
    logout: () => {},
    getUserInfo: async () => {},
    getUserInfoResponse: getResponseInitialState
});

export const useAuth = () => React.useContext(AuthContext);

export const AuthContextProvider: React.FC = ({ children }) => {
    const history = useHistory();
    const [auth, dispatchAuth] = React.useReducer(
        authReducer,
        authReducerInitialState,
        authReducerInit
    );
    const [logoutPost] = useFetch('/utLogoff', 'POST');
    const { alert, dispatchAlert, closeAlert } = useAlert();
    const [getUserInfoHandler, userInfoLoading] = useFetch(
        '/dbMemberDetails',
        'GET'
    );
    const [logoutAlert, setLogoutAlert] = React.useState<boolean>(false);

    const closeLogoutAlert = React.useCallback(() => setLogoutAlert(false), []);

    const logout = React.useCallback(async () => {
        setLogoutAlert(true);
        await logoutPost({
            data: {
                member_guid: auth.session.MemberGuid,
                session_guid: auth.session.SessionGuid
            }
        });
        dispatchAuth({ type: 'RESET_SESSION' });
        dispatchAuth({ type: 'RESET' });
        history.push('/login');
    }, [
        auth.session.MemberGuid,
        auth.session.SessionGuid,
        history,
        logoutPost
    ]);

    const checkValidSession = React.useCallback(
        (checkSession: sessionProps, type: 'login' | 'checkSession') => {
            type === 'login'
                ? dispatchAuth({
                      type: 'SET_SESSION',
                      session: checkSession
                  })
                : type === 'checkSession' &&
                  dispatchAuth({
                      type: 'UPDATE_ROLE',
                      Role: checkSession.Type
                  });
            if (history.location.pathname === '/login') {
                if (
                    checkSession.Role === 0 ||
                    checkSession.Role === 1 ||
                    checkSession.Type === 0 ||
                    checkSession.Type === 1
                ) {
                    setTimeout(() => {
                        history.push('/members/view');
                    }, 800);
                } else if (checkSession.Role === 2 || checkSession.Type === 2) {
                    setTimeout(() => {
                        history.push('/policies');
                    }, 800);
                }
            }
        },
        [history]
    );

    const getUserInfo = React.useCallback(async () => {
        const result = await getUserInfoHandler({
            q: `member_guid=${auth.session.MemberGuid}&&session_guid=${auth.session.SessionGuid}`
        });
        if (result.success) {
            dispatchAuth({
                type: 'SET_USER_INFO',
                userInfo: result.data[0]
            });
        } else {
            dispatchAlert({
                type: 'UPDATE_ALERT',
                message: result.msg,
                severity: 'error'
            });
            dispatchAlert({ type: 'OPEN' });
        }
    }, [
        auth.session.MemberGuid,
        auth.session.SessionGuid,
        dispatchAlert,
        getUserInfoHandler
    ]);

    const getUserInfoResponse = React.useMemo(() => {
        return {
            loading: userInfoLoading,
            alert: alert,
            closeAlert: closeAlert
        };
    }, [alert, closeAlert, userInfoLoading]);

    return (
        <AuthContext.Provider
            value={{
                ...auth,
                auth,
                dispatchAuth,
                logoutAlert,
                closeLogoutAlert,
                logout,
                checkValidSession,
                getUserInfo,
                getUserInfoResponse
            }}
        >
            {children}
        </AuthContext.Provider>
    );
};
